import React, { memo } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TableSortLabel,
	Box,
	Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";

const DataTable = ({
	data = [],
	columns = [],
	sortConfig = null,
	onSort = null,
	maxHeight = 600,
	stickyHeader = true,
	showRowNumbers = false,
	onRowClick = null,
	rowKey = "id",
	emptyStateIcon: EmptyIcon = null,
	emptyStateTitle = "No data available",
	emptyStateSubtitle = "",
	tableProps = {},
	containerProps = {},
	headerCellProps = {},
	rowProps = {},
	cellProps = {},
	rowOverlay = null, // Function: (row, rowIndex) => ReactNode or null
}) => {
	const [t] = useTranslation();

	// Helper function to get unique key for each row
	const getRowKey = (item, index) => {
		// If rowKey is provided and exists on the item, use it
		if (rowKey && item[rowKey] != null) {
			return item[rowKey];
		}
		// Fallback to row index
		return `row-${index}`;
	};

	const handleSort = (columnKey) => {
		if (onSort && columnKey) {
			onSort(columnKey);
		}
	};

	const renderCellContent = (item, column, rowIndex) => {
		if (column.render) {
			return column.render(item, rowIndex);
		}

		if (column.key) {
			return item[column.key] || "";
		}

		return "";
	};

	// ✅ Empty state
	if (!data || data.length === 0) {
		return (
			<Box sx={{ textAlign: "center", py: 8 }}>
				{EmptyIcon && (
					<EmptyIcon
						sx={{
							fontSize: 64,
							color: "text.secondary",
							mb: 2
						}}
					/>
				)}
				<Typography variant="h6" color="text.secondary" gutterBottom>
					{emptyStateTitle}
				</Typography>
				{emptyStateSubtitle && (
					<Typography variant="body2" color="text.secondary">
						{emptyStateSubtitle}
					</Typography>
				)}
			</Box>
		);
	}

	// ✅ All styling comes from theme now!
	return (
		<Paper
			sx={{
				width: "100%",
				overflow: "visible", // ✅ Changed to visible for ribbon
				...containerProps
			}}
		>
			<TableContainer sx={{ overflow: "visible" }}> {/* ✅ Added overflow visible */}
				<Table {...tableProps}>
					<TableHead
						sx={{
							position: "sticky",
							top: 0,
							zIndex: 10,
							backgroundColor: "background.paper"
						}}
					>
						<TableRow>
							{showRowNumbers && (
								<TableCell sx={headerCellProps}>
									#
								</TableCell>
							)}
							{columns.map((column, index) => (
								<TableCell
									key={column.key || index}
									align={column.align || "left"}
									sx={{
										minWidth: column.minWidth,
										width: column.width,
										...headerCellProps,
										...column.headerStyle,
									}}
								>
									{column.sortable && onSort ? (
										<TableSortLabel
											active={sortConfig?.key === column.key}
											direction={sortConfig?.key === column.key ? sortConfig.direction : "asc"}
											onClick={() => handleSort(column.key)}
										>
											{t(column.label)}
										</TableSortLabel>
									) : (
										t(column.label)
									)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((item, rowIndex) => (
							<TableRow
								key={getRowKey(item, rowIndex)}
								hover={!!onRowClick}
								onClick={onRowClick ? () => onRowClick(item, rowIndex) : undefined}
								sx={{
									position: 'relative', // ✅ Critical for ribbon positioning
									...(onRowClick && { cursor: "pointer" }),
									...rowProps,
									...(typeof rowProps === "function" ? rowProps(item, rowIndex) : {}),
								}}
							>
								{showRowNumbers && (
									<TableCell sx={cellProps}>
										{rowIndex + 1}
									</TableCell>
								)}
								{columns.map((column, colIndex) => (
									<TableCell
										key={column.key}
										align={column.align || "left"}
										sx={{
											// Add position relative and overflow visible to last cell for ribbon
											...(colIndex === columns.length - 1 && rowOverlay ? { position: 'relative', overflow: 'visible' } : {}),
											...cellProps,
											...column.cellStyle,
										}}
									>
										{renderCellContent(item, column, rowIndex)}
										{/* Render overlay in the last cell */}
										{colIndex === columns.length - 1 && rowOverlay && rowOverlay(item, rowIndex)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

export default memo(DataTable);